export type MedicineOrderStatus = "requested";

export type ShippingAddress = {
  line1: string;
  line2: string | null;
  city: string | null;
  region: string | null;
  postal_code: string | null;
  country_code: string | null;
};

export type ShippingAddressInput = {
  line1: string;
  line2?: string | null;
  city?: string | null;
  region?: string | null;
  postal_code?: string | null;
  country_code?: string | null;
};

export type MedicineOrder = {
  order_id: string;
  order_reference: string;
  document_id: string;
  document_filename: string | null;
  document_content_type: string | null;
  // The single configured pharmacy partner this order was submitted for -- a snapshot taken at
  // creation time on medopilot-medicine-order-service, not a live/global setting, so it always
  // reflects who this specific order was actually addressed to even if the configured partner
  // changes later.
  pharmacy_name: string;
  full_name: string;
  phone: string;
  email: string | null;
  shipping_address: ShippingAddress;
  order_note: string | null;
  disclosure_acknowledged_at: string | null;
  status: MedicineOrderStatus;
  created_at: string | null;
  updated_at: string | null;
};

export type CreateMedicineOrderInput = {
  document_id: string;
  full_name: string;
  phone: string;
  email?: string | null;
  shipping_address: ShippingAddressInput;
  order_note?: string | null;
  // Always true -- see Workspace.tsx's medicine order form, which only ever calls createOrder()
  // from the Place Order action, immediately below the disclosure. There is no separate consent
  // control to represent here.
  acknowledged: true;
};

type MedicineOrderResponse = {
  order?: Partial<MedicineOrder>;
};

type ListMedicineOrdersResponse = {
  orders?: Array<Partial<MedicineOrder>>;
};

export class MedicineOrderServiceError extends Error {
  constructor(message = "We could not reach the Medicine Orders service right now.") {
    super(message);
    this.name = "MedicineOrderServiceError";
  }
}

function textOrNull(value: unknown) {
  return typeof value === "string" && value.trim() ? value : null;
}

function normalizeShippingAddress(address: Partial<ShippingAddress> | undefined): ShippingAddress {
  return {
    line1: textOrNull(address?.line1) ?? "",
    line2: textOrNull(address?.line2),
    city: textOrNull(address?.city),
    region: textOrNull(address?.region),
    postal_code: textOrNull(address?.postal_code),
    country_code: textOrNull(address?.country_code),
  };
}

function normalizeMedicineOrder(record: Partial<MedicineOrder> | undefined): MedicineOrder {
  return {
    order_id: textOrNull(record?.order_id) ?? "",
    order_reference: textOrNull(record?.order_reference) ?? "",
    document_id: textOrNull(record?.document_id) ?? "",
    document_filename: textOrNull(record?.document_filename),
    document_content_type: textOrNull(record?.document_content_type),
    pharmacy_name: textOrNull(record?.pharmacy_name) ?? "",
    full_name: textOrNull(record?.full_name) ?? "",
    phone: textOrNull(record?.phone) ?? "",
    email: textOrNull(record?.email),
    shipping_address: normalizeShippingAddress(record?.shipping_address),
    order_note: textOrNull(record?.order_note),
    disclosure_acknowledged_at: textOrNull(record?.disclosure_acknowledged_at),
    status: "requested",
    created_at: textOrNull(record?.created_at),
    updated_at: textOrNull(record?.updated_at),
  };
}

function normalizeListMedicineOrdersResponse(data: ListMedicineOrdersResponse): MedicineOrder[] {
  if (!Array.isArray(data.orders)) {
    throw new MedicineOrderServiceError("We could not load your medicine orders.");
  }

  return data.orders.map(normalizeMedicineOrder).filter((order) => order.order_id);
}

async function requestMedicineOrderService<TBody>(
  apiBaseUrl: string,
  accessToken: string | undefined,
  path: string,
  method: "GET" | "POST",
  body?: TBody,
  fallbackMessage = "We could not reach the Medicine Orders service right now.",
  signal?: AbortSignal,
) {
  if (!accessToken) {
    throw new MedicineOrderServiceError("Please sign in again to manage your medicine orders.");
  }

  const response = await fetch(`${apiBaseUrl}${path}`, {
    method,
    signal,
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
      ...(body ? { "Content-Type": "application/json" } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    if (response.status === 400) {
      const errorBody = (await response.json().catch(() => null)) as { error?: { code?: unknown; message?: unknown } } | null;
      // invalid_document is the one create-time failure a user can actually act on (the
      // prescription they picked is missing or was moved to Trash since they chose it) -- surface
      // its own message; every other 400 falls back to the generic details prompt.
      if (errorBody?.error?.code === "invalid_document") {
        throw new MedicineOrderServiceError("We could not find that prescription in your Health Vault. Choose it again and retry.");
      }
      throw new MedicineOrderServiceError("Please check the order details and try again.");
    }

    if (response.status === 401 || response.status === 403) {
      throw new MedicineOrderServiceError("Please sign in again to manage your medicine orders.");
    }

    if (response.status === 404) {
      throw new MedicineOrderServiceError("We could not find that medicine order.");
    }

    throw new MedicineOrderServiceError(fallbackMessage);
  }

  return (await response.json()) as unknown;
}

export function createMedicineOrderServiceClient(apiBaseUrl: string, getAccessToken: () => string | undefined) {
  return {
    async listOrders(signal?: AbortSignal) {
      const data = (await requestMedicineOrderService(
        apiBaseUrl,
        getAccessToken(),
        "/v1/medicine-orders",
        "GET",
        undefined,
        "We could not load your medicine orders.",
        signal,
      )) as ListMedicineOrdersResponse;
      return normalizeListMedicineOrdersResponse(data);
    },
    async getOrder(orderId: string, signal?: AbortSignal) {
      const data = (await requestMedicineOrderService(
        apiBaseUrl,
        getAccessToken(),
        `/v1/medicine-orders/${encodeURIComponent(orderId)}`,
        "GET",
        undefined,
        "We could not load that medicine order.",
        signal,
      )) as MedicineOrderResponse;
      return normalizeMedicineOrder(data.order);
    },
    async createOrder(payload: CreateMedicineOrderInput) {
      const data = (await requestMedicineOrderService(
        apiBaseUrl,
        getAccessToken(),
        "/v1/medicine-orders",
        "POST",
        payload,
        "We could not place your medicine order right now.",
      )) as MedicineOrderResponse;
      return normalizeMedicineOrder(data.order);
    },
  };
}
